import { Book, CalendarDays, HelpCircle, FileText, GraduationCap, Layers, LayoutGrid, Library, MessageCircle, MessageSquarePlus, School, Settings, TrendingUp, Trophy, History, FileQuestion, Mail, Users, type LucideIcon, Megaphone } from "lucide-react";

export interface NavItem {
  name: string;
  path: string;
  icon: LucideIcon;
  /** Hidden while Focus mode is on. */
  essential?: boolean;
  description?: string;
}

/**
 * One navigation model for every surface (desktop rail, top bar, mobile tab
 * bar + More sheet). Four pillars carry the product; Ask Novari is a distinct
 * ambient action rather than a peer; planning and social tools are secondary;
 * account utilities live in the account menu.
 */
export const PROGRESS_NAV: NavItem = {
  name: "Progress",
  path: "/progress",
  icon: TrendingUp,
  essential: true,
};
export const CLASSROOM_NAV: NavItem = {
  name: "Classroom",
  path: "/classroom",
  icon: School,
};

export const PRIMARY_NAV: NavItem[] = [
  { name: "Today", path: "/dashboard", icon: LayoutGrid, essential: true },
  { name: "Study", path: "/study", icon: GraduationCap, essential: true },
  { name: "Library", path: "/documents", icon: Library, essential: true },
  PROGRESS_NAV,
];

export const SECONDARY_NAV: NavItem[] = [
  { name: "Planner", path: "/planner", icon: CalendarDays, essential: true },
  { name: "Curriculum", path: "/curriculum", icon: Book, essential: true },
  { name: "Community", path: "/groups", icon: Users },
  CLASSROOM_NAV,
];

export const ASK_NAV: NavItem = {
  name: "Ask Novari",
  path: "/chat",
  icon: MessageCircle,
  essential: true,
};

export const ACCOUNT_NAV: NavItem[] = [
  { name: "Settings", path: "/settings", icon: Settings, description: "Change your grade, subjects, syllabus, theme and tutor" },
  { name: "Help & FAQ", path: "/help", icon: HelpCircle },
  { name: "Send feedback", path: "/feedback", icon: MessageSquarePlus },
];

/** Discoverable destinations shared by the desktop menu and mobile sheet. */
export const EXPLORE_GROUPS: { name: string; items: NavItem[] }[] = [
  {
    name: "Study tools",
    items: [
      {
        name: "Quizzes",
        path: "/quiz-study",
        icon: GraduationCap,
        essential: true,
        description: "Practise a topic and review your answers",
      },
      {
        name: "Flashcards",
        path: "/flashcards",
        icon: Layers,
        essential: true,
        description: "Review what is due and remember more",
      },
      {
        name: "Summaries & Notes",
        path: "/summaries",
        icon: FileText,
        essential: true,
        description: "Summarise, explore a topic or write your own notes",
      },
      {
        name: "Past papers & questions",
        path: "/questions",
        icon: FileQuestion,
        essential: true,
        description: "Study from an exam paper or worksheet",
      },
      {
        ...CLASSROOM_NAV,
        description: "Lessons, notes, cards and quizzes in one session",
      },
    ],
  },
  {
    name: "Plan & connect",
    items: SECONDARY_NAV.filter((item) => item.path !== CLASSROOM_NAV.path),
  },
  {
    name: "Progress reports",
    items: [
      {
        ...PROGRESS_NAV,
        name: "Topic progress",
        description: "Your strengths and what to practise next",
      },
      {
        name: "Study activity",
        path: "/progress?view=analytics",
        icon: TrendingUp,
        essential: true,
        description: "Study time, results and learning patterns",
      },
      {
        name: "Achievements",
        path: "/achievements",
        icon: Trophy,
        description: "Your badges and milestones",
      },
    ],
  },
  {
    name: "Account & help",
    items: [
      ...ACCOUNT_NAV,
      { name: "Contact support", path: "/contact", icon: Mail, description: "Get help or report a problem" },
      {
        name: "Chat history",
        path: "/chat/history",
        icon: History,
        description: "Return to a conversation with your tutor",
      },
      {
        name: "What's new",
        path: "/whats-new",
        icon: Megaphone,
        description: "See the latest improvements to Novari",
      },
    ],
  },
];

/** Kids accounts only have access to a subset of routes (see KidsRouteGuard). */
export const KIDS_NAV: NavItem[] = [
  { name: "Home", path: "/dashboard", icon: LayoutGrid, essential: true },
  {
    name: "Quizzes",
    path: "/quiz-study",
    icon: GraduationCap,
    essential: true,
  },
  { name: "Flashcards", path: "/flashcards", icon: Layers, essential: true },
  { name: "Library", path: "/documents", icon: FileText, essential: true },
];

export function isNavActive(pathname: string, path: string) {
  if (path === "/dashboard") return pathname === "/dashboard";
  if (path === "/study")
    return (
      pathname === "/study" ||
      pathname.startsWith("/study/") ||
      pathname.startsWith("/quiz") ||
      pathname.startsWith("/flashcards") ||
      pathname.startsWith("/summaries") ||
      pathname.startsWith("/lesson")
    );
  if (path === "/progress")
    return (
      pathname.startsWith("/progress") || pathname.startsWith("/achievements")
    );
  return pathname === path || pathname.startsWith(path + "/");
}

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 76;
/** Inset between the floating rail and the viewport edge. */
export const SIDEBAR_INSET = 12;
